import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { Button, MantineProvider, createTheme } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";
import "@mantine/dates/styles.css";
import awsconfig from "./aws-exports";
import { Amplify } from "aws-amplify";

Amplify.configure(awsconfig);

const theme = createTheme({
  fontFamily: "Lato, sans-serif",
  colors: {
    brand: [
      "#F2F1F9",
      "#DFDCF1",
      "#BFBAE2",
      "#A097D4",
      "#8075C5",
      "#6052B7",
      "#4D4292",
      "#3A316E",
      "#201B41",
      "#131025",
    ],
    blue: [
      "#D6DDFF",
      "#ADBBFF",
      "#8599FF",
      "#5C77FF",
      "#3355FF",
      "#2944CC",
      "#1F3399",
      "#142266",
      "#0A1133",
      "#0D0D0D",
    ],
    purple: [
      "#E4D6FF",
      "#C9ADFF",
      "#AD85FF",
      "#925CFF",
      "#925CFF",
      "#5F29CC",
      "#471F99",
      "#301466",
      "#180A33",
      "#0D0D0D",
    ],
    violet: [
      "#F1D6FF",
      "#E4ADFF",
      "#D685FF",
      "#C95CFF",
      "#BB33FF",
      "#9629CC",
      "#701F99",
      "#4B1466",
      "#250A33",
      "#0D0D0D",
    ],
    red: [
      "#FDEDED",
      "#FACCCC",
      "#F59999",
      "#EF6666",
      "#EA3333",
      "#E50000",
      "#B70000",
      "#890000",
      "#5C0000",
      "#2E0000",
    ],
    pink: [
      "#FFD6DD",
      "#FFADBB",
      "#FF8599",
      "#FF5C77",
      "#FF3355",
      "#CC2944",
      "#991F33",
      "#661422",
      "#330A11",
      "#0D0D0D",
    ],
    orange: [
      "#FFE6CC",
      "#FFCC99",
      "#FFB366",
      "#FF9933",
      "#FF8000",
      "#CC6600",
      "#994D00",
      "#663300",
      "#331A00",
      "#0D0D0D",
    ],
    yellow: [
      "#FFFCEB",
      "#FFF5CC",
      "#FFEB99",
      "#FFE066",
      "#FFD633",
      "#FFCC00",
      "#CCA300",
      "#997A00",
      "#665200",
      "#332900",
    ],
    green: [
      "#EFFBEF",
      "#CCEBD6",
      "#99D6AD",
      "#66C285",
      "#33AD5C",
      "#009933",
      "#007A29",
      "#005C1F",
      "#003D14",
      "#001F0A",
    ],
    teal: [
      "#CCF5F5",
      "#99EBEB",
      "#66E0E0",
      "#33D6D6",
      "#00CCCC",
      "#00A3A3",
      "#007A7A",
      "#005252",
      "#002929",
      "#0D0D0D",
    ],
    cyan: [
      "#CCEEFF",
      "#99DDFF",
      "#66CCFF",
      "#33BBFF",
      "#00AAFF",
      "#0088CC",
      "#006699",
      "#004466",
      "#002233",
      "#0D0D0D",
    ],
    success: [
      "#E6F4EA",
      "#C0E2C8",
      "#99D1A6",
      "#73BF84",
      "#4CAF62",
      "#26A040",
      "#208933",
      "#1A7126",
      "#145A19",
      "#0E430C",
    ],
    error: [
      "#FDEDED",
      "#F9D6D6",
      "#F5BFBF",
      "#F1A8A8",
      "#ED9191",
      "#E97A7A",
      "#D66E6E",
      "#C36161",
      "#B05454",
      "#9D4747",
    ],
    info: [
      "#DDEEFF",
      "#BBDDFF",
      "#99CCFF",
      "#77BBFF",
      "#55AAFF",
      "#3399FF",
      "#2D82CC",
      "#266699",
      "#1F4D73",
      "#13334C",
    ],
    warning: [
      "#FFF5CC",
      "#FFEB99",
      "#FFE066",
      "#FFD633",
      "#FFCC00",
      "#CCA300",
      "#997A00",
      "#665200",
      "#332900",
      "#1A1400",
    ],
  },
  components: {
    /*
    Stepper: {
      styles: () => ({
        step: {
          fontSize: "1.8rem",
          fontWeight: 600,
        },
        stepLabel: {
          fontSize: "1.6rem",
          color: "#201b41",
        },
        stepDescription: {
          fontSize: "1.2rem",
          color: "#b2b2b2",
        },
        separator: {
          backgroundColor: "#b2b2b2",
          height: "4px",
        },
        steps: {
          padding: "10px 0",
        },
        stepIcon: {
          backgroundColor: "#201b41",
          color: "#ffffff",
        },
      }),
    },
     */
    Text: {
      styles: () => ({
        root: {
          fontSize: "1.6rem",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }),
    },
    Input: {
      styles: () => ({
        input: {
          fontSize: "1.2rem",
          padding: ".5rem",
          "::placeholder": {
            fontSize: "1.2rem",
          },
        },
        label: {
          fontSize: "1.2rem",
        },
      }),
    },
    TextInput: {
      styles: () => ({
        input: {
          fontSize: "1.2rem",
          padding: ".5rem",
          borderRadius: ".5rem",
          border: "1px solid #b2b2b2",
          "&::placeholder": {
            color: "#201b41",
            fontStyle: "italic",
          },
        },
        label: {
          fontSize: "1.2rem",
          fontWeight: 600,
          color: "#201b41",
        },
      }),
    },
    Textarea: {
      styles: () => ({
        input: {
          fontSize: "1.2rem",
          padding: ".5rem",
          borderRadius: ".5rem",
          border: "1px solid #b2b2b2",
          "&::placeholder": {
            color: "#201b41",
            fontStyle: "italic",
          },
        },
        label: {
          fontSize: "1.2rem",
          fontWeight: 600,
          color: "#201b41",
        },
      }),
    },
    Select: {
      styles: () => ({
        input: {
          fontSize: "1.2rem",
          padding: "1rem",
          borderRadius: ".5rem",
          border: "1px solid #b2b2b2",
        },
        dropdown: {
          borderRadius: ".5rem",
          border: "1px solid #b2b2b2",
        },
        item: {
          fontSize: "1.2rem",
          padding: "8px 12px",
          "&[data-selected]": {
            backgroundColor: "#6052B7",
            color: "white",
          },
          "&[data-hovered]": {
            backgroundColor: "#A097D4",
          },
        },
        label: {
          fontSize: "1.2rem",
          fontWeight: 600,
          color: "#201b41",
        },
      }),
    },
    DatePickerInput: {
      styles: () => ({
        input: {
          fontSize: "1.2rem",
          padding: ".2rem .5rem",
          borderRadius: ".5rem",
          border: "1px solid #b2b2b2",
        },
        calendarWrapper: {
          borderRadius: ".5rem",
          border: "1px solid #b2b2b2",
        },
        day: {
          fontSize: "1rem",
          padding: ".5rem",
          "&[data-selected]": {
            backgroundColor: "#6052B7",
            color: "white",
          },
          "&[data-hovered]": {
            backgroundColor: "#201b41",
            color: "#ffffff",
          },
        },
        label: {
          fontSize: "1.2rem",
          fontWeight: 600,
          color: "#201b41",
        },
      }),
    },
    // Notifications: Notifications.extend({
    //   styles: (theme, params) => {
    //     // Add logging to check values
    //     // console.log('Theme colors:', theme.colors);
    //     // console.log('Params color:', params.color);

    //     return {
    //       root: {
    //         backgroundColor: theme.colors[params.color || 'gray'][6],
    //         color: theme.white,
    //       },
    //     };
    //   },
    // }),
    /*
    Notifications: {
      styles: () => ({
        root: {
          fontSize: "1.4rem",
          padding: "1rem",
        },
        title: {
          fontSize: "1.6rem",
          fontWeight: 700,
        },
        description: {
          fontSize: "1.2rem",
        },
      }),
    },
     */
    Notification: {
      styles: {
        root: {
          padding: "1rem 1.5rem",
          minWidth: "300px",
        },
        label: {
          fontSize: "1.6rem",
          fontWeight: "bold",
          lineHeight: "1.4",
          marginBottom: "0.5rem",
          whiteSpace: "normal",
        },
        description: {
          fontSize: "1.4rem",
          lineHeight: "1.2",
          whiteSpace: "normal",
        },
        closeButton: {
          width: "1.6rem",
          height: "1.6rem",
        },
      },
    },
    Button: Button.extend({
      defaultProps: {
        color: "#686868",
        variant: "outline",
      },
    }),
  },
  // primaryColor: 'cyan',
});
/*
const darkThemeOverride = {
  colorScheme: "dark",
  colors: {
    dark: [
      "#d5d7e0",
      "#acaebf",
      "#8c8fa3",
      "#666980",
      "#4d4f66",
      "#34354a",
      "#2b2c3d",
      "#1d1e30",
      "#0c0d21",
      "#01010a",
    ],
    primary: ["#bb86fc", "#3700b3"],
  },
};
 */
const rootElement = document.getElementById("root");
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <MantineProvider theme={{ ...theme }} defaultColorScheme="light">
        <Notifications zIndex={10000} />
        <App />
      </MantineProvider>
    </React.StrictMode>,
  );
}
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
